import { useEffect, useState, type JSX } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { resolveViewerTier, useSettingsDrawer } from '@cca/ui';
import { api, ApiError, type MeResponse } from '../api-client';
import { RequestAccessPage } from '../modules/access/RequestAccessPage';
import { ParishSettings } from './ParishSettings';
import { Header } from './Header';
import { ModuleNav } from './ModuleNav';

export function Layout(): JSX.Element {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const settings = useSettingsDrawer();
  const { parishId } = useParams<{ parishId: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const activeModule = pathname.includes('/personnel') ? 'personnel' : 'identity';

  useEffect(() => {
    let isMounted = true;

    api
      .me()
      .then((data) => {
        if (!isMounted) return;
        setMe(data);
      })
      .catch((err: Error) => {
        if (!isMounted) return;
        // Unauthenticated — redirect to login instead of showing error
        if (err instanceof ApiError && err.status === 401) {
          navigate('/auth/login', { replace: true });
        } else {
          setError(err.message);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Login-audit heartbeat — drives "time active" in the registrar's sign-in
  // trail. Ping /me on mount, every 3 min while the tab is visible, and again
  // whenever it returns to the foreground. Best-effort; throttled server-side.
  useEffect(() => {
    const beat = () => {
      if (document.visibilityState === 'visible') void api.me().catch(() => {});
    };
    const id = window.setInterval(beat, 180_000);
    document.addEventListener('visibilitychange', beat);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', beat);
    };
  }, []);

  if (loading) {
    return <FrameMessage>Loading…</FrameMessage>;
  }

  if (error || !me) {
    return (
      <FrameMessage>
        <p className="text-ccaanz-red">Could not load your account.</p>
        <p className="mt-2 text-sm text-brand-muted">{error ?? 'Unknown error'}</p>
      </FrameMessage>
    );
  }

  // Signed in but not yet authorised to edit any parish → show the request flow.
  if (me.parishes.length === 0) {
    return <RequestAccessPage email={me.email} pendingRequests={me.pending_access_requests} />;
  }

  const activeParish = me.parishes.find((p) => p.id === parishId) ?? me.parishes[0] ?? null;
  const viewer = resolveViewerTier({ isRegistrar: me.is_registrar });

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <Header
        parishes={me.parishes}
        activeParishId={activeParish?.id ?? null}
        onParishChange={(id) => navigate(`/parish/${id}/identity`)}
        subNav={<ModuleNav activeModule={activeModule} vettingAccess={me.vetting_access} />}
        settingsButtonProps={settings.buttonProps}
      />
      <ParishSettings open={settings.open} onClose={settings.onClose} viewer={viewer} me={me} />
      <main className="mx-auto w-full max-w-5xl flex-1 px-7 py-8">
        <Outlet context={{ me, activeParish }} />
      </main>
      <footer className="border-t border-edge bg-white py-4 text-center text-[11px] uppercase tracking-[0.08em] text-ccaanz-grey">
        CCAANZ Parish Portal · {new Date().getFullYear()} ·{' '}
        <Link to="/privacy" className="underline">
          Privacy
        </Link>
      </footer>
    </div>
  );
}

function FrameMessage({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="max-w-md text-center">{children}</div>
    </div>
  );
}
